/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Página: Orders » Lista de Pedidos de Venda
 * ---------------------------------------------------------------------------
 * - Busca paginada com filtro de status e busca livre
 * - Ações rápidas: Avaliar (estoque/regras) e Liberar (onda)
 * - Link para detalhe do pedido
 * - Auto-refresh para monitores operacionais
 * ========================================================================== */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    listOrders,
    listPendingOrders,
    evaluateOrder,
    releaseOrder,
    type Order,
    type Page,
} from "@/api/orders";

const STATUS_OPTS: { value: string; label: string }[] = [
    { value: "", label: "Todos" },
    { value: "PENDING", label: "Pendentes" },
    { value: "PLANNED", label: "Planejados" },
    { value: "RELEASED", label: "Liberados" },
    { value: "CLOSED", label: "Encerrados" },
];

/** Helpers tolerantes a variações de modelo -------------------------------- */
function getCreatedAt(o: Order): string | undefined {
    const a = o as any;
    return a.created_at ?? a.created ?? a.createdAt ?? a.created_on ?? undefined;
}

function getRequireDate(o: Order): string | undefined {
    const a = o as any;
    return (
        a.require_date ??
        a.required_date ??
        a.request_date ??
        a.requested_date ??
        a.due_date ??
        undefined
    );
}

function getCustomerDisplay(o: Order): string {
    const a = o as any;
    return (
        a.customer_name ??
        a.customer?.name ??
        a.customer_name1 ??
        a.customer ??
        ""
    );
}

function getLinesCount(o: Order): number | undefined {
    const a = o as any;
    const n =
        a.lines?.length ??
        a.items?.length ??
        a.order_lines?.length ??
        a.details?.length ??
        a.positions?.length ??
        undefined;
    return typeof n === "number" ? n : undefined;
}

function getNotes(o: Order): string {
    const a = o as any;
    return String(a.notes ?? a.note ?? a.remark ?? "");
}

function getRefCode(o: Order): string {
    const a = o as any;
    return String(a.ref_code ?? a.reference ?? a.code ?? a.id ?? "");
}

/** Componente -------------------------------------------------------------- */
export default function OrdersList() {
    const [rows, setRows] = useState<Order[]>([]);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [auto, setAuto] = useState(true);
    const timer = useRef<number | null>(null);

    async function load() {
        setLoading(true);
        setError("");
        try {
            let page: Page<Order>;
            if (status === "PENDING") page = await listPendingOrders();
            else page = await listOrders({ search, status, page_size: 50 });
            setRows(page.results);
        } catch (e: any) {
            setError(e?.message || "Falha ao carregar pedidos");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        if (!auto) {
            if (timer.current) window.clearInterval(timer.current);
            timer.current = null;
            return;
        }
        timer.current = window.setInterval(load, 10_000) as unknown as number;
        return () => {
            if (timer.current) window.clearInterval(timer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auto]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q || status === "PENDING") return rows;
        return rows.filter((r) => {
            const ref = getRefCode(r).toLowerCase();
            const cust = getCustomerDisplay(r).toLowerCase();
            const note = getNotes(r).toLowerCase();
            return [ref, cust, note].some((s) => s.includes(q));
        });
    }, [rows, search, status]);

    async function onEvaluate(id: number) {
        try {
            setLoading(true);
            await evaluateOrder(id);
            await load();
            alert("Pedido avaliado / planejado com sucesso.");
        } catch (e: any) {
            alert(e?.message || "Falha ao avaliar o pedido.");
        } finally {
            setLoading(false);
        }
    }

    async function onRelease(id: number) {
        try {
            setLoading(true);
            await releaseOrder(id);
            await load();
            alert("Pedido liberado para onda.");
        } catch (e: any) {
            alert(e?.message || "Falha ao liberar o pedido.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="p-4 md:p-6">
            <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <h1 className="text-xl font-semibold">Pedidos de Venda</h1>
                <div className="flex items-center gap-2">
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por referência, cliente, nota..."
                        className="input input-bordered w-72 max-w-full"
                    />
                    <select
                        className="select select-bordered"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {STATUS_OPTS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                    <button className="btn btn-outline" onClick={load} disabled={loading}>
                        {loading ? "Atualizando..." : "Atualizar"}
                    </button>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={auto}
                            onChange={(e) => setAuto(e.target.checked)}
                        />
                        <span className="text-sm">Auto-refresh</span>
                    </label>
                </div>
            </div>

            {!!error && (
                <div className="mb-3 px-3 py-2 rounded bg-red-900/30 text-red-200 border border-red-700">
                    {error}
                </div>
            )}

            <div className="overflow-auto rounded-xl border border-neutral-800">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-900 text-neutral-100">
                        <tr>
                            <th className="text-left px-3 py-2 w-[140px]">Ref.</th>
                            <th className="text-left px-3 py-2">Cliente</th>
                            <th className="text-left px-3 py-2">Data req.</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Linhas</th>
                            <th className="text-left px-3 py-2">Criado</th>
                            <th className="text-left px-3 py-2 w-[180px]">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((o) => {
                            const createdRaw = getCreatedAt(o);
                            const created = createdRaw
                                ? new Date(createdRaw).toLocaleString()
                                : "—";

                            const reqRaw = getRequireDate(o);
                            const req = reqRaw ? new Date(reqRaw).toLocaleDateString() : "—";

                            const linesN = getLinesCount(o);
                            const lines = typeof linesN === "number" ? linesN : "—";

                            return (
                                <tr
                                    className="border-t border-neutral-800"
                                    key={(o as any).id ?? getRefCode(o)}
                                >
                                    <td className="px-3 py-2 font-mono">
                                        <Link to={`/orders/${(o as any).id}`} className="link">
                                            {getRefCode(o)}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2">{getCustomerDisplay(o)}</td>
                                    <td className="px-3 py-2">{req}</td>
                                    <td className="px-3 py-2">{(o as any).status ?? ""}</td>
                                    <td className="px-3 py-2">{lines}</td>
                                    <td className="px-3 py-2">{created}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2">
                                            <button
                                                className="btn btn-xs btn-outline"
                                                onClick={() => onEvaluate((o as any).id)}
                                                title="Avaliar/planejar pedido"
                                            >
                                                Avaliar
                                            </button>
                                            <button
                                                className="btn btn-xs btn-outline"
                                                onClick={() => onRelease((o as any).id)}
                                                title="Liberar para onda"
                                            >
                                                Liberar
                                            </button>
                                            <Link
                                                className="btn btn-xs"
                                                to={`/orders/${(o as any).id}`}
                                                title="Abrir detalhes"
                                            >
                                                Abrir
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="px-3 py-10 text-center text-neutral-400"
                                >
                                    Nenhum pedido encontrado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
